// ./src/components/features/EventsTable/EventsTable.tsx
import React, { useMemo } from "react";
import { Stack, Text } from "@mantine/core";

import {
  AppTable,
  SortConfig,
  AppTableColumn,
  RecordActions,
  EventStatusBadge,
} from "@/components/generic";
import { Event, formatDate, formatUTCTimeRange } from "@/utils";
import { EventRoomsInfoBadge } from "../EventRoomsInfoBadge";
import { EventTeamsInfoBadge } from "../EventTeamsInfoBadge";
import { TagsBadge } from "../TagsBadge";

export interface EventsTableProps {
  /** Array of events to display */
  events: Event[];

  /** Loading state for initial load */
  loading: boolean;

  /** Loading state for loading more items */
  loadingMore: boolean;

  /** Whether there are more items to load */
  hasMore: boolean;

  /** Current sort configuration */
  sortBy: SortConfig[];

  /** Callback when sort changes */
  onSortChange: (sortBy: SortConfig[]) => void;

  /** Callback to load more items */
  onLoadMore: () => void;

  /** Callback when duplicate action is clicked */
  onDuplicateEvent?: (event: Event) => void;

  /** Callback when edit action is clicked */
  onEditEvent?: (event: Event) => void;

  /** Callback when remove action is clicked */
  onRemoveEvent?: (event: Event) => void;

  /** Callback when view action is clicked */
  onViewEvent?: (event: Event) => void;

  /** Callback when view recurring event is clicked */
  onViewRecurringEvent?: (event: Event) => void;

  /** ID of event currently being removed */
  removingEventId?: string | null;

  hideOnNoRedcords?: boolean;

  /** Table size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
}

export const EventsTable: React.FC<EventsTableProps> = ({
  events,
  loading,
  loadingMore,
  hasMore,
  sortBy,
  onSortChange,
  onLoadMore,
  onDuplicateEvent,
  onEditEvent,
  onRemoveEvent,
  onViewEvent,
  onViewRecurringEvent,
  removingEventId,
  hideOnNoRedcords,
  size = "md",
}) => {
  // Define table columns
  const columns: AppTableColumn<Event>[] = useMemo(
    () => [
      {
        key: "name",
        label: "Event Name",
        sortable: true,
        flex: true,
        minWidth: 200,
        render: (value: any, record: Event) => (
          <Stack gap={0}>
            <Text fw={600}>{value || "Untitled Event"}</Text>
            {record.eventUid && (
              <Text size="xs" fw={300} c="gray.6">
                UID: {record.eventUid}
              </Text>
            )}
            <EventTeamsInfoBadge event={record} />
            <TagsBadge
              value={record.categories?.map((category) => category?.name ?? "")}
            />
          </Stack>
        ),
      },
      {
        key: "eventDate",
        label: "Date",
        sortable: true,
        width: 160,
        minWidth: 160,
        render: (value: any) => <Text>{formatDate(value)}</Text>,
      },
      {
        key: "startTime",
        label: "Time",
        width: 200,
        minWidth: 200,
        //hideOnMobile: true,
        render: (value: any, record: Event) => (
          <Text>
            {formatUTCTimeRange(value, record.endTime, "h:mm A", true)}
          </Text>
        ),
      },
      {
        key: "room",
        label: "Rooms",
        width: 200,
        minWidth: 200,
        //hideOnMobile: true,
        render: (_, record) => <EventRoomsInfoBadge event={record} />,
      },
      {
        key: "status",
        label: "Status",
        sortable: true,
        width: 150,
        minWidth: 150,
        align: "left",
        render: (value: any) => {
          return <EventStatusBadge status={value} />;
        },
      },
      {
        key: "actions",
        label: "",
        width: 200,
        minWidth: 200,
        align: "left",
        render: (_: any, record: Event) => (
          <RecordActions
            context={record}
            //onViewClick={onViewEvent}
            onDuplicateClick={onDuplicateEvent}
            onEditClick={onEditEvent}
            onRemoveClick={onRemoveEvent}
            onGoInClick={
              record.recurringEventId ? onViewRecurringEvent : undefined
            }
            removeInProgress={removingEventId === record.id}
          />
        ),
      },
    ],
    [
      onViewEvent,
      onEditEvent,
      onRemoveEvent,
      onViewRecurringEvent,
      removingEventId,
    ]
  );

  if (hideOnNoRedcords === true && (!events || events.length == 0)) {
    return null;
  }

  return (
    <AppTable
      data={events}
      hideOnNoData={true}
      columns={columns}
      loading={loading}
      hasMore={hasMore}
      loadingMore={loadingMore}
      onLoadMore={onLoadMore}
      sortBy={sortBy}
      onSortChange={onSortChange}
      multiSort={false}
      getRowKey={(event: any) => event?.id || ""}
      withBorder
      highlightOnHover
      size={size}
    />
  );
};
